import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Box from '@mui/material/Box'
import Avatar from '@mui/material/Avatar'
import Typography from '@mui/material/Typography'
import Container from '@mui/material/Container'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { alpha, useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useEffect, type ReactNode } from 'react'
import type { Shop } from '../shared/types'
import { useAuth } from './account/AuthContext'
import { useCart } from './cart/CartContext'
import LegalFooter from '../shared/components/LegalFooter'
import ModeToggle from '../shared/components/ModeToggle'
import { ChickMascot } from './components/brand'
import BottomNav, { BOTTOM_NAV_HEIGHT } from './components/BottomNav'

/** The shop's own identity (logo, name, banner, tagline) — no DukaaNo branding (§10). */
export default function StorefrontLayout({ shop, children }: { shop?: Shop; children: ReactNode }) {
  const navigate = useNavigate()
  const theme = useTheme()
  const { t } = useTranslation()
  const { isAuthenticated, session } = useAuth()
  const { itemCount } = useCart()
  const name = shop?.name ?? t('common.loading')
  const logoUrl = shop?.logoUrl
  const bannerUrl = shop?.bannerUrl
  const tagline = shop?.tagline
  const description = shop?.description
  const phone = shop?.phone
  const address = shop?.address
  // Hero headline = the shop's own tagline, falling back to its name. The terracotta panel,
  // mascot and "fresh today" pill are fixed design chrome (identical for every shop); all shop
  // copy stays data-driven from GET /api/storefront/shop — nothing tenant-specific is hardcoded.
  const headline = tagline || name
  const primary = theme.palette.primary.main
  const isDark = theme.palette.mode === 'dark'

  // The browser-tab title is the shop's own name, not "DukaaNo" (§10).
  useEffect(() => {
    if (shop?.name) {
      document.title = shop.name
    }
  }, [shop?.name])

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ bgcolor: 'background.paper', borderBottom: 'none', boxShadow: '0 2px 8px rgba(59,42,30,0.18)' }}
      >
        <Toolbar>
          {/* Logo + name act as the "home" link back to the shop from any sub-page. */}
          <Box
            onClick={() => navigate('/')}
            role="button"
            aria-label={t('storefront.goToShop')}
            sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, minWidth: 0, cursor: 'pointer' }}
          >
            {logoUrl ? (
              <Avatar variant="rounded" src={logoUrl} alt={name} sx={{ mr: 1.5 }} />
            ) : (
              <Avatar variant="rounded" sx={{ mr: 1.5, bgcolor: 'primary.main' }}>{name.charAt(0)}</Avatar>
            )}
            <Typography variant="h6" noWrap sx={{ fontWeight: 700, color: 'primary.main' }}>
              {name}
            </Typography>
          </Box>
          <ModeToggle color={theme.palette.text.primary} />
          {isAuthenticated ? (
            <Button
              color="inherit"
              startIcon={<PersonOutlineIcon />}
              onClick={() => navigate('/account')}
              sx={{ color: 'text.primary' }}
            >
              {session?.name?.split(' ')[0] ?? t('common.account')}
            </Button>
          ) : (
            <IconButton aria-label={t('common.logIn')} onClick={() => navigate('/login')} sx={{ color: 'text.primary' }}>
              <PersonOutlineIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/*
        Branded hero (§10). A terracotta panel + mascot for every shop; if the shop uploaded a
        banner it becomes the background behind a brand-tinted scrim so the white copy stays
        readable. Real content, above the fold — the storefront's LCP region, never lazy.
      */}
      <Container maxWidth="md" sx={{ pt: 2, px: { xs: 1.5, sm: 2 } }}>
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: '20px',
            p: { xs: 2.5, sm: 3 },
            minHeight: { xs: 140, sm: 160 },
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            // Sunny Chick: warm gold gradient + dark-red copy. Midnight Rooster: dark maroon + cream.
            // Over a shop banner, white copy on a brand-tinted scrim (both modes).
            color: bannerUrl ? 'common.white' : isDark ? '#F6EAD2' : '#7A2410',
            bgcolor: isDark ? '#22100c' : '#F7A72B',
            border: isDark ? '1px solid #4a2a1a' : 'none',
            backgroundImage: bannerUrl
              ? `linear-gradient(${alpha(primary, isDark ? 0.42 : 0.55)}, ${alpha(primary, isDark ? 0.72 : 0.8)}), url(${bannerUrl})`
              : isDark
                ? 'linear-gradient(135deg, #3a1512, #22100c)'
                : 'linear-gradient(135deg, #FFD23F, #F7A72B)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            boxShadow: isDark ? '0 8px 26px rgba(0,0,0,0.5)' : '0 10px 24px -12px rgba(220,150,30,0.6)',
          }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.15, textShadow: bannerUrl ? '0 1px 3px rgba(0,0,0,0.25)' : 'none' }}>
              {headline}
            </Typography>
            {description && (
              <Typography variant="body2" sx={{ mt: 0.75, opacity: 0.92 }}>
                {description}
              </Typography>
            )}
          </Box>
          <ChickMascot size={88} sx={{ flexShrink: 0, filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.18))' }} />
        </Box>
      </Container>

      <Box component="main">{children}</Box>

      {/* Policy links sit in the footer of EVERY page (payment-gateway auditors click through).
          The always-on BottomNav and (on the catalog) the CartBar are position:fixed and would
          cover the footer — pad the bottom to clear the nav, plus the ~72px cart bar when items. */}
      <Box
        component="footer"
        sx={{
          pt: 3,
          px: 2,
          pb: itemCount > 0 ? `${BOTTOM_NAV_HEIGHT + 88}px` : `${BOTTOM_NAV_HEIGHT + 24}px`,
          textAlign: 'center',
          color: 'text.secondary',
        }}
      >
        {address && <Typography variant="body2">{address}</Typography>}
        {phone && <Typography variant="body2">{phone}</Typography>}
        <LegalFooter />
      </Box>

      <BottomNav />
    </Box>
  )
}
